import {
  NICKNAME_MAX_LENGTH,
  NICKNAME_MIN_LENGTH,
  ROOM_CODE_LENGTH,
  normalizeNickname,
} from '@sprinters/shared';
import { unlockAudio } from '../../game/audio';
import { errorMessage, fr } from '../../i18n/fr';
import type { GameClient } from '../../net/GameClient';
import { loadNickname, saveNickname } from '../../net/session';
import type { AppState } from '../../state/store';
import { h } from '../dom';
import type { Screen } from './types';

export class HomeScreen implements Screen {
  readonly element: HTMLElement;

  private readonly nicknameInput: HTMLInputElement;
  private readonly codeInput: HTMLInputElement;
  private readonly createButton: HTMLButtonElement;
  private readonly joinButton: HTMLButtonElement;
  private readonly errorLine: HTMLElement;
  private readonly statusPill: HTMLElement;
  private busy = false;
  private connected = false;

  constructor(private readonly client: GameClient) {
    this.nicknameInput = h('input', {
      id: 'nickname',
      type: 'text',
      class: 'input',
      placeholder: 'Votre pseudo',
      maxlength: NICKNAME_MAX_LENGTH,
      autocomplete: 'nickname',
      value: loadNickname(),
      onKeydown: (event: KeyboardEvent) => {
        if (event.key !== 'Enter') return;
        if (this.codeInput.value.trim()) void this.join();
        else void this.create();
      },
    });
    this.nicknameInput.value = loadNickname();

    const initialCode = new URLSearchParams(location.search).get('room') ?? '';
    this.codeInput = h('input', {
      id: 'room-code',
      type: 'text',
      class: 'input input-code',
      placeholder: 'CODE',
      maxlength: ROOM_CODE_LENGTH,
      autocomplete: 'off',
      autocapitalize: 'characters',
      spellcheck: 'false',
      onInput: () => {
        this.codeInput.value = this.codeInput.value.toUpperCase();
      },
      onKeydown: (event: KeyboardEvent) => {
        if (event.key === 'Enter') void this.join();
      },
    });
    this.codeInput.value = initialCode.toUpperCase().slice(0, ROOM_CODE_LENGTH);

    this.createButton = h(
      'button',
      { class: 'btn btn-primary btn-lg', type: 'button', onClick: () => void this.create() },
      'Créer un salon',
    );
    this.joinButton = h(
      'button',
      { class: 'btn btn-secondary', type: 'button', onClick: () => void this.join() },
      'Rejoindre',
    );
    this.errorLine = h('p', { class: 'form-error', role: 'alert' });
    this.statusPill = h('span', { class: 'pill' });

    this.element = h(
      'main',
      { class: 'home' },
      h(
        'section',
        { class: 'hero' },
        h('h1', { class: 'logo' }, 'SPRINTERS', h('span', { class: 'logo-v' }, 'V2')),
        h('p', { class: 'tagline' }, 'Le sprint multijoueur en tournoi à élimination directe.'),
        this.statusPill,
      ),
      h(
        'section',
        { class: 'card home-card' },
        h('label', { class: 'label', for: 'nickname' }, 'Pseudo'),
        this.nicknameInput,
        this.createButton,
        h('div', { class: 'divider' }, h('span', null, 'ou')),
        h('label', { class: 'label', for: 'room-code' }, 'Rejoindre avec un code'),
        h('div', { class: 'join-row' }, this.codeInput, this.joinButton),
        this.errorLine,
      ),
      h(
        'section',
        { class: 'card how-to' },
        h('h2', null, 'Comment jouer ?'),
        h(
          'ol',
          null,
          h('li', null, 'Créez un salon et partagez le code avec vos amis.'),
          h(
            'li',
            null,
            'Le tournoi se joue par séries de 8 coureurs : les 4 premiers se qualifient.',
          ),
          h(
            'li',
            null,
            'Alternez ',
            h('kbd', null, '←'),
            ' et ',
            h('kbd', null, '→'),
            ' le plus vite possible pour accélérer.',
          ),
          h(
            'li',
            null,
            'Attention : appuyer sur les deux touches en même temps vous fait ',
            h('strong', null, 'tomber'),
            ' et vous fait perdre un temps précieux !',
          ),
        ),
      ),
    );
  }

  update(state: AppState): void {
    this.connected = state.connection === 'connected';
    this.statusPill.textContent = fr.connection[state.connection];
    this.statusPill.className = `pill pill-${state.connection}`;
    this.syncButtons();
  }

  destroy(): void {
    // Rien à libérer : les écouteurs sont attachés aux éléments de l'écran.
  }

  private syncButtons(): void {
    const disabled = this.busy || !this.connected;
    this.createButton.disabled = disabled;
    this.joinButton.disabled = disabled;
  }

  private readNickname(): string | null {
    const nickname = normalizeNickname(this.nicknameInput.value);
    if (nickname.length < NICKNAME_MIN_LENGTH || nickname.length > NICKNAME_MAX_LENGTH) {
      this.showError(
        `Le pseudo doit contenir entre ${NICKNAME_MIN_LENGTH} et ${NICKNAME_MAX_LENGTH} caractères.`,
      );
      this.nicknameInput.focus();
      return null;
    }
    saveNickname(nickname);
    return nickname;
  }

  private showError(message: string): void {
    this.errorLine.textContent = message;
  }

  private async run(action: () => ReturnType<GameClient['createRoom']>): Promise<void> {
    this.busy = true;
    this.syncButtons();
    this.showError('');
    unlockAudio();
    const result = await action();
    this.busy = false;
    this.syncButtons();
    if (result.ok) {
      history.replaceState(null, '', location.pathname);
    } else {
      this.showError(errorMessage(result.error));
    }
  }

  private async create(): Promise<void> {
    const nickname = this.readNickname();
    if (nickname) await this.run(() => this.client.createRoom(nickname));
  }

  private async join(): Promise<void> {
    const nickname = this.readNickname();
    if (!nickname) return;
    const code = this.codeInput.value.trim();
    if (code.length !== ROOM_CODE_LENGTH) {
      this.showError(`Le code du salon contient ${ROOM_CODE_LENGTH} caractères.`);
      this.codeInput.focus();
      return;
    }
    await this.run(() => this.client.joinRoom(code, nickname));
  }
}
